<template>
  <v-card>
    <v-card-title>
      <span class="secondary--text ml-6 font-weight-bold text-subtitle-1">
        {{
          $t(
            'fleet_management.points_of_interest.create_points_of_interest.new_point_of_interest'
          )
        }}
      </span>
    </v-card-title>
    <v-divider></v-divider>

    <v-card-text>
      <v-form ref="form" v-model="valid" v-if="showForm">
        <v-container>
          <v-row>
            <v-col cols="12">
              <div class="d-flex justify-center">
                <span class="primary--text text-caption">
                  {{
                    $t(
                      'fleet_management.points_of_interest.create_points_of_interest.information_required_fields'
                    )
                  }}
                </span>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <span class="tertiary--text text-body-2">
                {{
                  $t(
                    'fleet_management.points_of_interest.create_points_of_interest.name_required_fields'
                  )
                }}
              </span>
              <v-text-field
                v-model.trim="pointsOfInterestName"
                @blur="checkName"
                :counter="40"
                :rules="nameRules"
                filled
                dense
                required
                color="tertiary"
                ref="pointsOfInterestName"
              />
            </v-col>
            <v-col cols="4">
              <span class="tertiary--text text-body-2">
                {{
                  $t(
                    'fleet_management.points_of_interest.create_points_of_interest.type_required_fields'
                  )
                }}
              </span>
              <v-select
                v-model.trim="selectedCategory"
                :items="category"
                return-object
                required
                dense
                filled
                item-text="name"
                color="tertiary"
                :rules="[v => !!v.name.trim() || 'Seleccione un tipo']"
                @change="handleResetPointsOfInterest"
              >
              </v-select>
            </v-col>
            <v-col cols="2">
              <span class="tertiary--text text-body-2">
                {{
                  $t(
                    'fleet_management.points_of_interest.create_points_of_interest.labels.poi_created_field_icon'
                  )
                }}
              </span>
              <v-select
                v-model="selectedCategoryIcons"
                :items="categoryIcons"
                return-object
                dense
                filled
                item-text="name"
                color="tertiary"
                @change="handleResetPointsOfInterest"
              >
                <template v-slot:item="{ item }">
                  <div
                    class="d-flex justify-center align-center"
                    style="width: 100%;"
                  >
                    <v-icon>{{ item.icon }}</v-icon>
                  </div>
                </template>

                <template v-slot:selection="{ item }">
                  <div class="d-flex justify-center align-center">
                    <v-icon>{{ item.icon }}</v-icon>
                  </div>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row justify="center" class="mt-0">
            <v-col cols="auto">
              <span class="tertiary--text text-body-2">
                {{
                  $t(
                    'fleet_management.points_of_interest.create_points_of_interest.labels.poi_created_field_color'
                  )
                }}
              </span>
              <v-color-picker
                v-model="pointColor"
                dot-size="40"
                hide-canvas
                hide-inputs
                hide-mode-switch
                hide-sliders
                mode="hexa"
                show-swatches
                swatches-max-height="100"
                :disabled="loading"
                ref="colorPickerRef"
              ></v-color-picker>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <span class="tertiary--text text-center">
                {{
                  $t(
                    'fleet_management.points_of_interest.create_points_of_interest.labels.select_point_long_lat'
                  )
                }}
              </span>
            </v-col>
          </v-row>
          <v-row class="mt-0">
            <v-col>
              <v-card color="#f1f1f1" elevation="0">
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12">
                      <span class="tertiary--text text-body-2">
                        {{
                          $t(
                            'fleet_management.points_of_interest.create_points_of_interest.labels.poi_created_field_location'
                          )
                        }}:
                      </span>
                      <span class="secondary--text">
                        {{ getGeocodeResult }}
                      </span>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="6">
                      <span class="tertiary--text text-body-2">
                        {{
                          $t(
                            'fleet_management.points_of_interest.create_points_of_interest.labels.poi_created_field_lat'
                          )
                        }}:
                      </span>
                      <span class="secondary--text">
                        {{ getPointOfInterestGeoCoordinates.lat }}
                      </span>
                    </v-col>
                    <v-col cols="6">
                      <span class="tertiary--text text-body-2">
                        {{
                          $t(
                            'fleet_management.points_of_interest.create_points_of_interest.labels.poi_created_field_long'
                          )
                        }}:
                      </span>

                      <span class="secondary--text">{{
                        getPointOfInterestGeoCoordinates.lng
                      }}</span>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <span class="tertiary--text text-body-2">
                {{
                  $t(
                    'fleet_management.points_of_interest.create_points_of_interest.labels.poi_created_field_description'
                  )
                }}
              </span>
              <v-textarea
                v-model="description"
                filled
                dense
                color="tertiary"
                ref="pointsOfInterestDescription"
                rows="2"
              >
              </v-textarea>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <hr class="divider-dotted" />
            </v-col>
          </v-row>
          <v-row>
            <v-col class="my-3">
              <v-row justify="space-between">
                <v-col cols="auto">
                  <v-btn
                    @click="cancel"
                    outlined="true"
                    color="secondary"
                    rounded
                    >{{
                      $t(
                        'fleet_management.points_of_interest.create_points_of_interest.labels.form_new_poi_cancel_button'
                      )
                    }}</v-btn
                  >
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    color="tertiary white--text"
                    rounded
                    :loading="loading"
                    :disabled="pointsValid || !valid"
                    @click="savePointsOfInterest"
                    >{{
                      $t(
                        'fleet_management.points_of_interest.create_points_of_interest.labels.form_new_poi_accept_button'
                      )
                    }}</v-btn
                  >
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
      <v-row v-else>
        <v-col cols="12">
          <h2 class="secondary--text text-center mt-9">
            {{
              $t(
                'fleet_management.points_of_interest.create_points_of_interest.alerts.points_of_interest_created'
              )
            }}
          </h2>
        </v-col>
        <v-col>
          <h4 class="secondary--text text-center">
            {{ pointsOfInterestName }}
          </h4>
        </v-col>
        <v-col cols="12" class="d-flex justify-center px-7 my-16">
          <v-row class="text-center">
            <v-col md="6">
              <v-btn
                outlined="true"
                color="secondary"
                rounded
                :to="{ name: 'PointDetail', params: { id: newPointId } }"
                >{{
                  $t(
                    'fleet_management.points_of_interest.create_points_of_interest.labels.poi_created_action_detail_button'
                  )
                }}</v-btn
              >
            </v-col>
            <v-col md="6">
              <v-btn
                color="tertiary white--text"
                rounded
                :to="{ name: 'PointsList' }"
                >{{
                  $t(
                    'fleet_management.points_of_interest.create_points_of_interest.labels.poi_created_action_list_button'
                  )
                }}</v-btn
              >
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
    <Dialog
      :closeButtonText="dialog.closeButtonText"
      @cancelButtonPressed="showDialog = false"
      :oneButton="dialog.oneButton"
      @oneButtonAction="cancelDialog"
      :oneButtonText="dialog.oneButtonText"
      :showDialog="showDialog"
      @submitButtonPressed="cancelPointsOfInterest"
      :submitButtonText="dialog.acceptButtonText"
      :text="dialog.text"
      :type="dialog.type"
    />
  </v-card>
</template>

<script>
import { i18n } from '@leapsight/portal'
import Dialog from '@/components/dialog/Dialog'
import { mapActions, mapGetters } from 'vuex'
import { SvgIcon } from '@leapsight/styleguide'
import { PointsHelpers } from '@/helpers/mainExports.js'
import store from '@/store'

export default {
  name: 'CreatePointsOfInterest',
  components: {
    Dialog,
    SvgIcon
  },
  data() {
    return {
      pointColor: '#000000',
      selectPointColor: null,
      description: '',
      selectedCategory: { name: '' },
      selectedCategoryIcons: { name: '', icon: '' },
      category: [
        { name: 'Clientes' },
        { name: 'Depósitos' },
        { name: 'Empresas' },
        { name: 'Estación de Servicio' },
        { name: 'Otras' },
        { name: 'Paradas autorizadas' },
        { name: 'Paradores' },
        { name: 'Parque Industrial' },
        { name: 'Plantas' },
        { name: 'Transportes' }
      ],
      categoryIcons: [
        { name: 'Clientes', icon: 'mdi-account' },
        { name: 'Depósitos', icon: 'mdi-warehouse' },
        { name: 'Empresas', icon: 'mdi-office-building' },
        {
          name: 'Estación de Servicio',
          icon: 'mdi-gas-station-in-use-outline'
        },
        { name: 'Otras', icon: 'mdi-star-outline' },
        { name: 'Paradas autorizadas', icon: 'mdi-check-decagram-outline' },
        { name: 'Paradores', icon: 'mdi-map-marker-radius-outline' },
        { name: 'Parque Industrial', icon: 'mdi-domain' },
        { name: 'Fábrica', icon: 'mdi-factory' },
        { name: 'Transportes', icon: 'mdi-truck-delivery-outline' }
      ],
      latitude: '',
      longitude: '',
      dialogDefault: {
        text: '',
        closeButtonText: i18n.t('general.dialogComponent.closeButtonText'),
        acceptButtonText: i18n.t('general.dialogComponent.acceptButtonText'),
        type: '',
        loading: false,
        oneButton: false,
        oneButtonText: ''
      },
      dialog: {
        text: '',
        closeButtonText: i18n.t('general.dialogComponent.closeButtonText'),
        acceptButtonText: i18n.t('general.dialogComponent.acceptButtonText'),
        type: '',
        loading: false,
        oneButton: false,
        oneButtonText: ''
      },
      errorName: '',
      pointsOfInterestName: '',
      loading: false,
      nameRules: [
        v =>
          !!v ||
          i18n.t('fleet_management.persons.new_person.rules.name_required'),
        v =>
          (v && v.length >= 2 && v.length <= 40) ||
          i18n.t(
            'fleet_management.geofences.create_geofence.rules.character_length'
          )
      ],
      descriptionRules: [
        v =>
          (v && v.length <= 20) ||
          i18n.t(
            'fleet_management.geofences.create_geofence.rules.character_length'
          )
      ],
      cancelSubmit: false,
      routeTo: { name: 'PointsList' },
      showForm: true,
      showDialog: false,
      valid: true,
      location: '',
      newPointId: '',
      pointCreatedSuccessful: false
    }
  },
  computed: {
    ...mapGetters('pointOfInterest', [
      'getPoints',
      'getGeocodeResult',
      'getPointOfInterestGeoCoordinates'
    ]),
    pointsValid() {
      return (
        !this.pointsOfInterestName ||
        !this.selectedCategory ||
        !this.getPointOfInterestGeoCoordinates.lat ||
        !this.getPointOfInterestGeoCoordinates.lng
      )
    }
  },
  methods: {
    ...mapActions('ui', ['setSnackbar']),
    cancel() {
      this.showDialog = true
      this.dialog.type = 'warning'
      this.dialog.text = i18n.t(
        'fleet_management.points_of_interest.create_points_of_interest.alerts.card_text_cancel_new_poi'
      )
      this.dialog.closeButtonText = i18n.t(
        'fleet_management.points_of_interest.create_points_of_interest.alerts.card_cancel_button'
      )
      this.dialog.acceptButtonText = i18n.t(
        'fleet_management.points_of_interest.create_points_of_interest.alerts.card_accept_button'
      )
    },
    cancelDialog() {
      this.showDialog = false
      Object.assign(this.dialog, this.dialogDefault)
    },
    cancelPointsOfInterest() {
      this.pointCreatedSuccessful = true
      this.$router.push({ name: this.routeTo.name })
    },
    handleResetPointsOfInterest() {
      if (this.selectedCategory.name === 'Otras') {
        const otherIcon = this.categoryIcons.find(icon => icon.name === 'Otras')
        if (otherIcon) {
          this.selectedCategoryIcons = otherIcon
        }
      }
    },
    checkName() {
      this.errorName = ''
      this.cancelSubmit = false
      const name = this.pointsOfInterestName
      const points = this.getPoints

      if (points.length > 0) {
        for (let index = 0; index < points.length; index++) {
          const element = points[index]
          if (element.label === name) {
            this.showDialog = true
            this.dialog.type = 'warning'
            this.dialog.text = i18n.t(
              'fleet_management.points_of_interest.create_points_of_interest.errors.name_match'
            )

            this.dialog.oneButton = true
            this.dialog.oneButtonText = i18n.t(
              'general.dialogComponent.acceptButtonText'
            )
            this.errorName = i18n.t(
              'fleet_management.points_of_interest.create_points_of_interest.errors.name_match'
            )
            this.cancelSubmit = true
            break
          }
        }
      }
    },
    async savePointsOfInterest() {
      if (
        this.$refs.form.validate() &&
        this.getPointOfInterestGeoCoordinates.lat !== null &&
        this.getPointOfInterestGeoCoordinates.lng !== null &&
        !this.cancelSubmit &&
        this.pointsOfInterestName !== null
      ) {
        this.loading = true

        const payload = {
          label: this.pointsOfInterestName || i18n.t(
              'fleet_management.points_of_interest.create_points_of_interest.errors.no_name_assigned'
          ),
          color: this.pointColor || '#000000',
          category: this.selectedCategory.name || 'Otras',
          icon: this.selectedCategoryIcons.icon || 'mdi-star-outline',
          description: this.description || '',
          geometry: {
            coordinates: [
              this.getPointOfInterestGeoCoordinates.lat || 0,
              this.getPointOfInterestGeoCoordinates.lng || 0
            ],
            type: 'Point'
          }
        }

        PointsHelpers.createPoints(payload)
          .then(response => {
            this.newPointId = response.id
            this.pointCreatedSuccessful = true
            this.loading = false
            this.showForm = false

            this.$root.$emit('hideMarker')
          })
          .catch(error => {
            this.dialog.loading = false
            this.showDialog = false
            const errorText = PointsHelpers.getErrorMessageByCode(error.error)
            const snackbar = {
              text: errorText
            }
            store.dispatch('ui/setSnackbar', snackbar)
          })
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.pointCreatedSuccessful) {
      next()
    } else {
      this.routeTo = to
      this.showDialog = true
      this.dialog.type = 'warning'
      this.dialog.text = i18n.t(
        'fleet_management.points_of_interest.create_points_of_interest.alerts.card_text_cancel_new_poi'
      )
      this.dialog.closeButtonText = i18n.t(
        'fleet_management.points_of_interest.create_points_of_interest.alerts.card_cancel_button'
      )
      this.dialog.acceptButtonText = i18n.t(
        'fleet_management.points_of_interest.create_points_of_interest.alerts.card_accept_button'
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.orange--text {
  color: var(--orange) !important;
}
.dialog-attention {
  border: 2px solid var(--orange);
}
</style>
